// ClientHubPortal — Browning Electric's CLIENT-facing hub (/hub).
//
// This surface is for clients, not crew: it renders outside PermissionsProvider,
// Layout, and the crew route registry (see the /hub branch in App.jsx). It never
// imports the Supabase client — every read/write goes through /api/client-portal,
// which owns auth (app-issued session tokens) and strict per-client scoping.
//
// Views: sign-in (email → magic link) → redeem (?hub_token= behind a click) →
// dashboard (quotes / scheduled work / invoices) → quote detail with
// multi-option selection + accept.

import { useState, useEffect, useCallback } from "react";

const SESSION_KEY = "elecbid.hubSession";

// ── Session storage ───────────────────────────────────────────────────────────
function readSession() {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    return s?.token ? s : null;
  } catch { return null; }
}
function writeSession(s) {
  try { window.localStorage.setItem(SESSION_KEY, JSON.stringify(s)); } catch { /* private mode */ }
}
function clearSession() {
  try { window.localStorage.removeItem(SESSION_KEY); } catch { /* private mode */ }
}

async function hubFetch(action, payload = {}, token = null) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers["x-hub-session"] = token;
  const r = await fetch("/api/client-portal", {
    method: "POST",
    headers,
    body: JSON.stringify({ action, ...payload }),
  });
  const j = await r.json().catch(() => ({}));
  return { status: r.status, ...j };
}

// ── Formatting ────────────────────────────────────────────────────────────────
const fmtMoney = (n) =>
  "$" + Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  return dt.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
};
const fmtDateTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return "";
  return dt.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" }) +
    " · " + dt.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit" });
};

// ── Shared styles (client-facing: light, clean, mobile-first) ─────────────────
const C = {
  bg: "#f5f6f2",
  card: "#ffffff",
  ink: "#15181d",
  sub: "#5c6470",
  line: "#e4e7e0",
  accent: "#a5ce3a",
  accentInk: "#5f7d16",
  danger: "#c9533a",
  ok: "#3f9142",
};
const S = {
  page: {
    minHeight: "100vh", background: C.bg, color: C.ink,
    fontFamily: "-apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
  },
  shell: { maxWidth: 720, margin: "0 auto", padding: "0 16px 64px" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 0 14px", borderBottom: `1px solid ${C.line}`, marginBottom: 20,
  },
  brand: { fontSize: 20, fontWeight: 800, letterSpacing: "0.02em" },
  brandSub: { fontSize: 12, color: C.sub, marginTop: 2 },
  card: {
    background: C.card, border: `1px solid ${C.line}`, borderRadius: 12,
    padding: "16px 18px", marginBottom: 12, boxShadow: "0 1px 2px rgba(20,25,10,0.04)",
  },
  h2: { fontSize: 15, fontWeight: 700, margin: "26px 0 10px", color: C.ink },
  chip: (bg, ink) => ({
    display: "inline-block", padding: "3px 10px", borderRadius: 999,
    fontSize: 12, fontWeight: 600, background: bg, color: ink, whiteSpace: "nowrap",
  }),
  btn: {
    display: "inline-block", background: C.accent, color: "#15220a", border: "none",
    padding: "12px 24px", borderRadius: 9, fontWeight: 700, fontSize: 15, cursor: "pointer",
  },
  btnGhost: {
    display: "inline-block", background: "transparent", color: C.sub,
    border: `1px solid ${C.line}`, padding: "8px 16px", borderRadius: 9,
    fontWeight: 600, fontSize: 13, cursor: "pointer",
  },
  input: {
    width: "100%", boxSizing: "border-box", padding: "12px 14px", fontSize: 16,
    border: `1px solid ${C.line}`, borderRadius: 9, background: "#fff", color: C.ink,
  },
  err: { color: C.danger, fontSize: 14, marginTop: 10 },
  sub: { color: C.sub, fontSize: 13 },
  stickyBar: {
    position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 40,
    background: C.card, borderTop: `1px solid ${C.line}`,
    boxShadow: "0 -4px 16px rgba(20,25,10,0.08)", padding: "12px 0",
  },
  stickyInner: {
    maxWidth: 720, margin: "0 auto", padding: "0 16px",
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14,
  },
};

function Header({ onSignOut }) {
  return (
    <div style={S.header}>
      <div>
        <div style={S.brand}>
          BROWNING <span style={{ color: C.accentInk }}>ELECTRIC</span>
        </div>
        <div style={S.brandSub}>Client 360</div>
      </div>
      {onSignOut && (
        <button style={S.btnGhost} onClick={onSignOut}>Sign out</button>
      )}
    </div>
  );
}

function StatusChip({ label, tone }) {
  const tones = {
    action: { bg: "#f3f8e2", ink: C.accentInk },
    ok: { bg: "#e8f3e8", ink: C.ok },
    warn: { bg: "#fbeae6", ink: C.danger },
    neutral: { bg: "#eef0ea", ink: C.sub },
  };
  const t = tones[tone] || tones.neutral;
  return <span style={S.chip(t.bg, t.ink)}>{label}</span>;
}

// ── Sign-in (request a magic link) ────────────────────────────────────────────
function SignInView() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true); setErr(null);
    const r = await hubFetch("request-link", { email: email.trim() });
    setBusy(false);
    if (r.ok) setSent(true);
    else setErr(r.error || "Something went wrong — please try again.");
  }

  return (
    <div style={S.page}>
      <div style={{ ...S.shell, maxWidth: 440 }}>
        <Header />
        <div style={{ ...S.card, padding: "26px 22px" }}>
          {sent ? (
            <>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Check your email</div>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: C.sub }}>
                If <strong style={{ color: C.ink }}>{email.trim()}</strong> is on file with us, a sign-in
                link is on its way. The link expires in 30 minutes.
              </p>
              <p style={{ ...S.sub, marginTop: 14 }}>
                Wrong email? <button style={{ ...S.btnGhost, padding: "4px 10px" }} onClick={() => { setSent(false); }}>Try another</button>
              </p>
            </>
          ) : (
            <>
              <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Sign in</div>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: C.sub, marginBottom: 18 }}>
                Enter the email address we have on file for you and we'll send you a secure
                sign-in link — no password needed.
              </p>
              <form onSubmit={submit}>
                <input
                  style={S.input}
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
                <button style={{ ...S.btn, width: "100%", marginTop: 12 }} disabled={busy} type="submit">
                  {busy ? "Sending…" : "Email me a sign-in link"}
                </button>
              </form>
              {err && <div style={S.err}>{err}</div>}
            </>
          )}
        </div>
        <p style={{ ...S.sub, textAlign: "center" }}>
          Questions? Call or email us — we're happy to help.
        </p>
      </div>
    </div>
  );
}

// ── Redeem (magic link lands here — token spent only on the click) ────────────
function RedeemView({ token, onSignedIn, onGiveUp }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function redeem() {
    if (busy) return;
    setBusy(true); setErr(null);
    const r = await hubFetch("redeem", { token });
    setBusy(false);
    if (r.ok && r.session_token) {
      const sess = {
        token: r.session_token,
        email: r.email || null,
        name: r.client_name || null,
        first_name: r.first_name || null,
      };
      writeSession(sess);
      onSignedIn(sess);
    } else {
      setErr(r.error || "This link has expired — request a new one.");
    }
  }

  return (
    <div style={S.page}>
      <div style={{ ...S.shell, maxWidth: 440 }}>
        <Header />
        <div style={{ ...S.card, padding: "26px 22px", textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Welcome back</div>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: C.sub, marginBottom: 20 }}>
            Click below to open your Client 360.
          </p>
          <button style={S.btn} onClick={redeem} disabled={busy}>
            {busy ? "Signing in…" : "Open my Client 360"}
          </button>
          {err && (
            <>
              <div style={S.err}>{err}</div>
              <button
                style={{ ...S.btnGhost, marginTop: 12 }}
                onClick={() => onGiveUp && onGiveUp()}
              >
                Request a new link
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Proposal presentation pieces (hero/intro, per-option photos, attachments) ─
function PhotoStrip({ photos, height = 100 }) {
  if (!photos || !photos.length) return null;
  return (
    <div style={{ display: "flex", gap: 8, overflowX: "auto", marginTop: 12, paddingBottom: 2 }}>
      {photos.map((p, i) => (
        <a key={i} href={p.url} target="_blank" rel="noreferrer" style={{ flex: "0 0 auto" }} title={p.caption || "Photo"}>
          <img src={p.url} alt={p.caption || "Photo"} loading="lazy"
            style={{ height, width: "auto", maxWidth: 240, borderRadius: 8, objectFit: "cover", border: `1px solid ${C.line}`, display: "block" }} />
        </a>
      ))}
    </div>
  );
}

function ProposalIntro({ proposal }) {
  if (!proposal || (!proposal.hero_url && !proposal.intro_headline && !proposal.intro_body)) return null;
  return (
    <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
      {proposal.hero_url && (
        <img src={proposal.hero_url} alt="" style={{ width: "100%", maxHeight: 220, objectFit: "cover", display: "block" }} />
      )}
      {(proposal.intro_headline || proposal.intro_body) && (
        <div style={{ padding: "18px" }}>
          {proposal.intro_headline && <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 6 }}>{proposal.intro_headline}</div>}
          {proposal.intro_body && <div style={{ fontSize: 14, lineHeight: 1.6, color: "#2c323b", whiteSpace: "pre-wrap" }}>{proposal.intro_body}</div>}
        </div>
      )}
    </div>
  );
}

function ProposalExtras({ proposal }) {
  if (!proposal) return null;
  const { attachments = [], previous_work = [], sections = [] } = proposal;
  return (
    <>
      {sections.map((s, i) => (
        <div key={`sec-${i}`} style={S.card}>
          {s.title && <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{s.title}</div>}
          {s.body && <div style={{ fontSize: 13.5, lineHeight: 1.6, color: "#2c323b", whiteSpace: "pre-wrap" }}>{s.body}</div>}
        </div>
      ))}
      {previous_work.length > 0 && (
        <>
          <div style={S.h2}>Some of our recent work</div>
          <div style={S.card}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8 }}>
              {previous_work.map((w, i) => (
                <a key={i} href={w.url} target="_blank" rel="noreferrer" title={w.caption || "Previous work"}>
                  <img src={w.url} alt={w.caption || "Previous work"} loading="lazy"
                    style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.line}`, display: "block" }} />
                  {w.caption && <div style={{ ...S.sub, fontSize: 11, marginTop: 4 }}>{w.caption}</div>}
                </a>
              ))}
            </div>
          </div>
        </>
      )}
      {attachments.length > 0 && (
        <>
          <div style={S.h2}>Attachments</div>
          <div style={S.card}>
            {attachments.map((a, i) => (
              <a key={i} href={a.url} target="_blank" rel="noreferrer"
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0",
                  borderTop: i ? `1px solid ${C.line}` : "none", color: C.accentInk, textDecoration: "none", fontWeight: 600, fontSize: 14 }}>
                <span style={{ fontSize: 18 }}>📎</span>
                <span>{a.filename || a.caption || "Attachment"}</span>
              </a>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ── Quote detail + option selection ───────────────────────────────────────────
function QuoteDetailView({ session, quoteId, onBack, onAccepted }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [acceptedNow, setAcceptedNow] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await hubFetch("quote-detail", { quote_id: quoteId }, session.token);
      if (!alive) return;
      if (r.ok && r.quote) {
        setData(r.quote);
        const rec = r.quote.options?.find((o) => o.recommended);
        setSelected(r.quote.accepted?.option_key || rec?.key || r.quote.options?.[0]?.key || null);
      } else {
        setErr(r.error || "Couldn't load this quote.");
      }
    })();
    return () => { alive = false; };
  }, [quoteId, session.token]);

  async function accept() {
    if (busy) return;
    setBusy(true); setErr(null);
    const r = await hubFetch(
      "accept-quote",
      { quote_id: quoteId, option_key: data.is_multi_option ? selected : null },
      session.token
    );
    setBusy(false); setConfirming(false);
    if (r.ok) {
      setAcceptedNow(r.accepted || {});
      onAccepted();
    } else {
      setErr(r.error || "Couldn't record your approval — please try again.");
    }
  }

  const accepted = acceptedNow || data?.accepted || null;
  const alreadyApproved = data && (data.status === "approved" || data.status === "converted");
  const selectedOption = data?.options?.find((o) => o.key === selected) || null;
  // The price the sticky footer reflects: the chosen option (multi) or the quote (single).
  const summary = data ? (data.is_multi_option ? selectedOption : data) : null;
  const depositPct = data ? Math.round((data.deposit_rate || 0.35) * 100) : 35;

  return (
    <div style={S.page}>
      <div style={S.shell}>
        <Header />
        <button style={{ ...S.btnGhost, marginBottom: 14 }} onClick={onBack}>← Back to dashboard</button>

        {err && !data && <div style={S.card}><div style={S.err}>{err}</div></div>}
        {!data && !err && <div style={{ ...S.card, color: C.sub }}>Loading quote…</div>}

        {data && (
          <>
            <div style={{ ...S.card, padding: "20px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>{data.title}</div>
                  <div style={{ ...S.sub, marginTop: 4 }}>Quote #{data.number}</div>
                </div>
                <div>
                  {accepted ? <StatusChip label="Accepted — thank you!" tone="ok" />
                    : alreadyApproved ? <StatusChip label={data.status_label} tone="ok" />
                    : <StatusChip label={data.status_label || "Awaiting your approval"} tone="action" />}
                </div>
              </div>
            </div>

            <ProposalIntro proposal={data.proposal} />

            {data.is_multi_option ? (
              <>
                <div style={S.h2}>
                  {accepted || alreadyApproved ? "Options" : "Choose the option that suits you best"}
                </div>
                {data.options.map((o) => {
                  const isSel = selected === o.key;
                  const isAcceptedOpt = accepted && accepted.option_key === o.key;
                  const selectable = !accepted && !alreadyApproved;
                  return (
                    <div
                      key={o.key}
                      onClick={() => selectable && setSelected(o.key)}
                      style={{
                        ...S.card,
                        cursor: selectable ? "pointer" : "default",
                        border: `2px solid ${isSel && selectable ? C.accent : isAcceptedOpt ? C.ok : C.line}`,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
                        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                          {selectable && (
                            <span style={{
                              marginTop: 3, width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                              border: `2px solid ${isSel ? C.accentInk : "#b8bfae"}`,
                              background: isSel ? C.accent : "#fff",
                            }} />
                          )}
                          <div>
                            <div style={{ fontWeight: 700, fontSize: 15 }}>
                              {o.label}
                              {o.recommended && (
                                <span style={{ ...S.chip("#f3f8e2", C.accentInk), marginLeft: 8 }}>Recommended</span>
                              )}
                              {isAcceptedOpt && (
                                <span style={{ ...S.chip("#e8f3e8", C.ok), marginLeft: 8 }}>Your choice</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontWeight: 800, fontSize: 16 }}>{fmtMoney(o.total)}</div>
                          <div style={{ ...S.sub, fontSize: 11 }}>{fmtMoney(o.subtotal)} + GST</div>
                        </div>
                      </div>
                      {o.description && (
                        <div style={{
                          marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.line}`,
                          fontSize: 13.5, lineHeight: 1.65, whiteSpace: "pre-wrap", color: "#2c323b",
                        }}>
                          {o.description}
                        </div>
                      )}
                      <PhotoStrip photos={o.photos} />
                    </div>
                  );
                })}
              </>
            ) : (
              <div style={S.card}>
                <div style={{ fontSize: 13.5, lineHeight: 1.65, whiteSpace: "pre-wrap", color: "#2c323b" }}>
                  {data.text}
                </div>
              </div>
            )}

            <ProposalExtras proposal={data.proposal} />

            {(accepted || alreadyApproved) && (
              <div style={{ ...S.card, background: "#f0f7ec", border: `1px solid #cfe3c4` }}>
                <div style={{ fontWeight: 700, color: C.ok, marginBottom: 4 }}>
                  {accepted ? "Approval received" : "Already approved"}
                </div>
                <div style={{ fontSize: 13.5, color: "#3c5238", lineHeight: 1.5 }}>
                  {accepted
                    ? "Thanks! We've been notified and will be in touch shortly to schedule the work."
                    : "This quote has been approved. We'll be in touch about scheduling if we haven't been already."}
                </div>
              </div>
            )}

            {/* Spacer so the fixed summary bar never covers the last card */}
            <div style={{ height: 148 }} />

            {/* Sticky price summary — reflects the selected option, always visible */}
            {summary && (
              <div style={S.stickyBar}>
                <div style={S.stickyInner}>
                  <div style={{ minWidth: 0 }}>
                    {data.is_multi_option && (
                      <div style={{ fontSize: 12.5, fontWeight: 700, color: C.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {summary.label}
                      </div>
                    )}
                    <div style={{ ...S.sub, fontSize: 12 }}>
                      Subtotal {fmtMoney(summary.subtotal)} · GST {fmtMoney(summary.gst)}
                    </div>
                    <div style={{ display: "flex", gap: 14, alignItems: "baseline", marginTop: 2, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 800, fontSize: 19 }}>{fmtMoney(summary.total)}</span>
                      <span style={{ fontSize: 12.5, color: C.accentInk, fontWeight: 600 }}>
                        Deposit to start {fmtMoney(summary.deposit)} ({depositPct}%)
                      </span>
                    </div>
                  </div>
                  <div style={{ flexShrink: 0 }}>
                    {accepted || alreadyApproved ? (
                      <span style={S.chip("#e8f3e8", C.ok)}>✓ Accepted</span>
                    ) : confirming ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch" }}>
                        <button style={{ ...S.btn, padding: "10px 18px" }} onClick={accept} disabled={busy}>
                          {busy ? "Recording…" : "Confirm — accept"}
                        </button>
                        <button style={{ ...S.btnGhost, padding: "6px 12px" }} onClick={() => setConfirming(false)} disabled={busy}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        style={{ ...S.btn, opacity: data.is_multi_option && !selected ? 0.5 : 1 }}
                        disabled={data.is_multi_option && !selected}
                        onClick={() => setConfirming(true)}
                      >
                        {data.is_multi_option ? "Accept this option" : "Accept quote"}
                      </button>
                    )}
                  </div>
                </div>
                {err && <div style={{ ...S.err, textAlign: "center", margin: "8px 16px 0" }}>{err}</div>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
function DashboardView({ session, onSignOut, onOpenQuote, refreshKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    const r = await hubFetch("hub-data", {}, session.token);
    if (r.status === 401) { onSignOut(true); return; }
    if (r.ok) { setData(r); setErr(null); }
    else setErr(r.error || "Couldn't load your info — please refresh.");
  }, [session.token, onSignOut]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const firstName = data?.client?.first_name || session.first_name || null;
  const quotes = data?.quotes || [];
  const jobs = data?.jobs || [];
  const invoices = data?.invoices || [];

  const needsApproval = quotes.filter((q) => q.kind === "smart" && !q.accepted && q.status === "awaiting_response");
  const otherQuotes = quotes.filter((q) => !needsApproval.includes(q));
  const upcomingVisits = jobs
    .flatMap((j) => j.visits.filter((v) => !v.is_complete && v.start_at && new Date(v.start_at) > new Date())
      .map((v) => ({ ...v, jobTitle: j.title, jobNum: j.job_num })))
    .sort((a, b) => String(a.start_at).localeCompare(String(b.start_at)))
    .slice(0, 6);
  const openInvoices = invoices.filter((i) => !i.is_paid);
  const paidInvoices = invoices.filter((i) => i.is_paid).slice(0, 10);

  return (
    <div style={S.page}>
      <div style={S.shell}>
        <Header onSignOut={() => onSignOut(false)} />

        {!data && !err && <div style={{ ...S.card, color: C.sub }}>Loading your info…</div>}
        {err && <div style={S.card}><div style={S.err}>{err}</div></div>}

        {data && (
          <>
            <div style={{ fontSize: 22, fontWeight: 800, margin: "4px 0 2px" }}>
              {firstName ? `Hi ${firstName},` : "Welcome,"}
            </div>
            <div style={{ ...S.sub, marginBottom: 8 }}>
              Here's where everything stands with your electrical work.
            </div>

            {needsApproval.length > 0 && (
              <>
                <div style={S.h2}>⚡ Waiting on you</div>
                {needsApproval.map((q) => (
                  <div key={q.id} style={{ ...S.card, border: `2px solid ${C.accent}`, cursor: "pointer" }}
                    onClick={() => onOpenQuote(q.id)}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 700 }}>{q.title}</div>
                        <div style={{ ...S.sub, marginTop: 3 }}>
                          Quote #{q.number}
                          {q.is_multi_option ? ` · ${q.options.length} options to choose from` : q.subtotal ? ` · ${fmtMoney(q.subtotal * 1.05)}` : ""}
                        </div>
                      </div>
                      <button style={{ ...S.btn, padding: "9px 18px", fontSize: 14 }}>
                        {q.is_multi_option ? "Choose an option" : "Review & approve"}
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}

            {upcomingVisits.length > 0 && (
              <>
                <div style={S.h2}>📅 Upcoming visits</div>
                <div style={S.card}>
                  {upcomingVisits.map((v, i) => (
                    <div key={i} style={{
                      display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap",
                      padding: "8px 0", borderTop: i ? `1px solid ${C.line}` : "none",
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{v.jobTitle}</div>
                      <div style={{ ...S.sub, fontWeight: 600 }}>{fmtDateTime(v.start_at)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {otherQuotes.length > 0 && (
              <>
                <div style={S.h2}>Your quotes</div>
                {otherQuotes.map((q, i) => (
                  <div
                    key={q.id || `jb-${q.number}-${i}`}
                    style={{ ...S.card, cursor: q.kind === "smart" ? "pointer" : "default" }}
                    onClick={() => q.kind === "smart" && onOpenQuote(q.id)}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{q.title}</div>
                        <div style={{ ...S.sub, marginTop: 3 }}>
                          Quote #{q.number}{q.sent_date ? ` · ${fmtDate(q.sent_date)}` : ""}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        {q.accepted ? <StatusChip label={q.accepted.option_label ? `Accepted — ${q.accepted.option_label}` : "Accepted"} tone="ok" />
                          : <StatusChip
                              label={q.status_label || "Sent"}
                              tone={q.status === "approved" || q.status === "converted" ? "ok" : "neutral"}
                            />}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {jobs.length > 0 && (
              <>
                <div style={S.h2}>Your jobs</div>
                {jobs.slice(0, 15).map((j) => (
                  <div key={j.job_num} style={S.card}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{j.title}</div>
                        <div style={{ ...S.sub, marginTop: 3 }}>
                          Job #{j.job_num}
                          {j.is_done && j.closed_date ? ` · completed ${fmtDate(j.closed_date)}` : ""}
                        </div>
                      </div>
                      <StatusChip label={j.status_label} tone={j.is_done ? "ok" : "neutral"} />
                    </div>
                  </div>
                ))}
              </>
            )}

            {(openInvoices.length > 0 || paidInvoices.length > 0) && (
              <>
                <div style={S.h2}>Invoices</div>
                {openInvoices.map((inv, i) => (
                  <div key={`o-${inv.invoice_number}-${i}`} style={{ ...S.card, border: inv.is_overdue ? `1px solid #eec4ba` : `1px solid ${C.line}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>Invoice #{inv.invoice_number}</div>
                        <div style={{ ...S.sub, marginTop: 3 }}>
                          {inv.issued_date ? `Issued ${fmtDate(inv.issued_date)}` : ""}
                          {inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ""}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 800, fontSize: 15 }}>{fmtMoney(inv.balance)}</div>
                        <StatusChip label={inv.status_label} tone={inv.is_overdue ? "warn" : "neutral"} />
                      </div>
                    </div>
                  </div>
                ))}
                {paidInvoices.map((inv, i) => (
                  <div key={`p-${inv.invoice_number}-${i}`} style={{ ...S.card, opacity: 0.75 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>Invoice #{inv.invoice_number}</div>
                        <div style={{ ...S.sub, marginTop: 3 }}>{inv.issued_date ? fmtDate(inv.issued_date) : ""}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{fmtMoney(inv.total)}</div>
                        <StatusChip label="Paid" tone="ok" />
                      </div>
                    </div>
                  </div>
                ))}
                <p style={{ ...S.sub, marginTop: 6 }}>
                  Questions about an invoice? Just reply to any of our emails or give us a call.
                </p>
              </>
            )}

            {quotes.length === 0 && jobs.length === 0 && invoices.length === 0 && (
              <div style={{ ...S.card, textAlign: "center", padding: "30px 20px", color: C.sub }}>
                Nothing here yet — once we've quoted or scheduled work for you, it'll show up on this page.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function ClientHubPortal() {
  // Capture the magic-link token ONCE, then strip it from the address bar
  // immediately so it doesn't linger in browser history, bookmarks, or a Referer
  // header. It stays in component state (still single-use + 30-min server TTL,
  // redeemed only on an explicit click). A refresh before redeeming just means
  // requesting a fresh link — the safe trade.
  const [hubToken, setHubToken] = useState(
    () => new URLSearchParams(window.location.search).get("hub_token")
  );
  const [session, setSession] = useState(() => readSession());
  const [openQuoteId, setOpenQuoteId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (hubToken && window.location.search) {
      window.history.replaceState({}, "", "/hub");
    }
  }, [hubToken]);

  const signOut = useCallback((expired) => {
    const s = readSession();
    clearSession();
    setSession(null);
    setOpenQuoteId(null);
    if (!expired && s?.token) {
      hubFetch("logout", {}, s.token).catch(() => {});
    }
  }, []);

  if (hubToken && !session) {
    return (
      <RedeemView
        token={hubToken}
        onSignedIn={(s) => { setHubToken(null); setSession(s); }}
        onGiveUp={() => setHubToken(null)}
      />
    );
  }
  if (!session) return <SignInView />;
  if (openQuoteId) {
    return (
      <QuoteDetailView
        session={session}
        quoteId={openQuoteId}
        onBack={() => setOpenQuoteId(null)}
        onAccepted={() => setRefreshKey((k) => k + 1)}
      />
    );
  }
  return (
    <DashboardView
      session={session}
      onSignOut={signOut}
      onOpenQuote={(id) => setOpenQuoteId(id)}
      refreshKey={refreshKey}
    />
  );
}
